import classNames from 'classnames';
import Decimal from 'decimal.js';
import { closeSnackbar } from 'notistack';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import Button from '../../../../../../components/Button';
import { t } from '../../../../../../lang';
import HelperService from '../../../../../../services/HelperService';
import TypesService from '../../../../../../services/TypesService';
import SwapButton from '../../../../../SwapButton';

import './SendAmountInput.scss';

const CurrenciesById = TypesService.CurrenciesById;
const balanceNotEnoughForAmountErrorText = 'Not enough balance';
const balanceNotEnoughForAmountAndFeeErrorText =
	'Not enough funds to cover the fee';
const feeAddressAmountNotEnoughForFee =
	'Not enough {currency} funds to cover the fee';

const SendAmountInput = ({
	asset = {},
	account = {},
	destinationAddress = '',
	amount = '0',
	feeData = { Amount: 0, Fee: 0 },
	isMaxAmountSetted = false,
	calculateCommission,
	setIsMaxAmountSetted,
	setAmountToStore,
	setFeeData,
	minAmountUsd = 0,
}) => {
	const [inputInUsd, setInputInUsd] = useState(false);
	const [displayedErrorText, setDisplayedErrorText] = useState('');

	const inputRef = useRef(null);
	const valueBox = useRef(null);
	const notEnoughNotification = useRef(null);

	const modals = useSelector(store => store.app.modals);
	const quotes = useSelector(store => store.app.quotes);

	const currencyData = CurrenciesById[asset.Id];
	const feeCurrencyData = CurrenciesById[feeData.CurrencyId] || {};
	const currencyRate = quotes.find(quote => quote.Id === asset.Id)?.Rate || 0;

	const currencyPrecision = currencyData.Precision;
	const inputPrecision = inputInUsd
		? TypesService.FiatPrecision
		: currencyPrecision;
	const convertedPrecision = inputInUsd
		? currencyPrecision
		: TypesService.FiatPrecision;

	const formatToFloor = (value, precision) =>
		HelperService.FormatCrypto(value, precision, 'floor');

	const formattingByPrecision = (amount, precision = inputPrecision) => {
		const formatingRegExp = new RegExp('(\\.[^.]{' + precision + '}).*');
		if (typeof amount === 'string')
			return amount.replace(formatingRegExp, '$1');
		if (typeof amount === 'number')
			return new Decimal(amount).toString().replace(formatingRegExp, '$1');
		return '0';
	}; // string

	const [inputAmount, setInputAmount] = useState(formattingByPrecision(amount)); // string

	const decimalConvertedAmount = inputInUsd
		? new Decimal(amount)
		: new Decimal(amount).mul(currencyRate); // Decimal
	const convertedAmountString = decimalConvertedAmount.toString(); // string
	const convertedAmount = decimalConvertedAmount.toNumber(); // string

	const usdAmount = inputInUsd ? convertedAmount : Number(inputAmount);

	const inputCurrency = inputInUsd ? '$' : currencyData.Name;
	const convertedCurrency = inputInUsd ? currencyData.Name : '$';

	const feeCalculateInBack = currencyData?.FeeType === 'back_calculating';
	const feeConfiguratingInFront =
		currencyData?.FeeType === 'front_configurating';

	const isBalanceEnoughForAmount = amount <= account?.Amount;
	const feeAmountForValidate =
		feeData.Loading && !feeData.FeeRedacted ? 0 : feeData.Fee;
	const isBalanceEnoughForAmountAndFee =
		asset.Id === feeData.CurrencyId
			? amount + feeAmountForValidate <= account?.Amount
			: isBalanceEnoughForAmount;
	const isFeeAddressAmountEnoughForFee =
		feeAmountForValidate <= feeData.Amount ||
		feeAmountForValidate <= feeData.GsBalance;
	const needAmountCoverFee =
		asset.Id === feeData.CurrencyId &&
		!(
			isMaxAmountSetted &&
			((feeCalculateInBack && !currencyData.Standart) ||
				feeConfiguratingInFront)
		);
	const needwWaitForCalculateFee = !feeData.FeeRedacted && feeData.Loading;
	const amountError =
		!isBalanceEnoughForAmount ||
		(!isBalanceEnoughForAmountAndFee &&
			needAmountCoverFee &&
			!needwWaitForCalculateFee);

	const focusInput = () => {
		inputRef.current.focus();
		inputRef.current.setSelectionRange(inputAmount.length, inputAmount.length);
	};

	const removeNotEnoughNotification = () => {
		closeSnackbar(notEnoughNotification.current);
		notEnoughNotification.current = null;
		setDisplayedErrorText('');
	};

	const showNotEnoughNotification = (
		text = balanceNotEnoughForAmountErrorText
	) => {
		if (displayedErrorText === text) return;
		removeNotEnoughNotification();
		setDisplayedErrorText(text);

		const notifficationProps = {
			className: 'SendAmountInput-NotEnough',
			icon: 'EXCLAMATIONMARK_CIRCLE_FILL',
			title: t('Attention'),
			description: t(text, { currency: feeCurrencyData.Name }),
			iconSize: 32,
			persist: true,
			visibleClose: true,
			autoHideDuration: 0,
			onClose: removeNotEnoughNotification,
		};

		if (notEnoughNotification.current) {
			setTimeout(
				() =>
					(notEnoughNotification.current =
						HelperService.showNotification(notifficationProps)),
				250
			);
		} else {
			notEnoughNotification.current =
				HelperService.showNotification(notifficationProps);
		}
	};

	const checkNeedShowNotiffication = () => {
		if (!isBalanceEnoughForAmount) {
			showNotEnoughNotification(balanceNotEnoughForAmountErrorText);
		} else if (!needwWaitForCalculateFee) {
			if (!isBalanceEnoughForAmountAndFee && needAmountCoverFee) {
				showNotEnoughNotification(balanceNotEnoughForAmountAndFeeErrorText);
			} else if (!isFeeAddressAmountEnoughForFee) {
				showNotEnoughNotification(feeAddressAmountNotEnoughForFee);
			} else {
				removeNotEnoughNotification();
			}
		} else {
			removeNotEnoughNotification();
		}
	};

	const updateAmount = (newAmount, actualInputInUsd = inputInUsd) => {
		const itIsSwap = actualInputInUsd !== inputInUsd;
		const inputPrecision = actualInputInUsd
			? TypesService.FiatPrecision
			: currencyPrecision;

		const commaReplaced = newAmount.replace(',', '.');
		const irrelevantSymbolRemoved = commaReplaced.replace(/[^.\d]/g, '');
		const excessDotRemoved = irrelevantSymbolRemoved.replace(
			/(?<!^[^.]*)\./g,
			''
		);
		const excessStartZerosRemoved = excessDotRemoved.replace(/^0+(?=\d)/, '');
		const startZeroAddingIfDotInStart = excessStartZerosRemoved.replace(
			/^\./,
			'0.'
		);
		const notEmptyAmount = startZeroAddingIfDotInStart || '0';
		const formatedForCurrency = formattingByPrecision(
			notEmptyAmount,
			inputPrecision
		);

		setInputAmount(formatedForCurrency);

		const numericAmount = Number(notEmptyAmount);

		const amountInCurrecy = actualInputInUsd
			? new Decimal(notEmptyAmount).dividedBy(currencyRate).toNumber()
			: numericAmount;
		const isAmountMax =
			Number(formattingByPrecision(amountInCurrecy, currencyPrecision)) ===
			Number(formattingByPrecision(account.Amount, currencyPrecision));
		setIsMaxAmountSetted(isAmountMax);

		const amountToStore = isAmountMax
			? account.Amount
			: itIsSwap
			? amount
			: actualInputInUsd
			? new Decimal(notEmptyAmount).dividedBy(currencyRate).toNumber()
			: numericAmount;

		if (amountToStore !== amount) {
			setAmountToStore(amountToStore);

			if (feeCalculateInBack || feeConfiguratingInFront) {
				if (amountToStore) {
					setFeeData({ ...feeData, Loading: true });
					calculateCommission(
						asset.Id,
						account.Address,
						destinationAddress,
						amountToStore,
						feeData
					);
				} else if (!feeData.FeeRedacted) {
					setFeeData({ ...feeData, Fee: 0, DefaultFee: 0 });
					calculateCommission(
						asset.Id,
						account.Address,
						destinationAddress,
						0,
						feeData
					);
				}
			}
		}

		if (!String(newAmount)) setTimeout(() => focusInput());
	};

	const setAmount = e => {
		updateAmount(e.target.value);
	};

	const setMaxAmount = () => {
		const maxInputAmount = inputInUsd
			? new Decimal(account.Amount).mul(currencyRate).toNumber()
			: account.Amount;

		updateAmount(new Decimal(maxInputAmount).toString());
		focusInput();
	};

	const swapInputCurrency = () => {
		const newInputInUsd = !inputInUsd;
		updateAmount(convertedAmountString, newInputInUsd);
		setInputInUsd(newInputInUsd);
		focusInput();
	};

	useEffect(() => {
		setTimeout(() => {
			focusInput();
		}, 250);

		return () => {
			if (notEnoughNotification.current) removeNotEnoughNotification();
		};
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		checkNeedShowNotiffication();
	}, [amount, feeData]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (feeCalculateInBack && !Number(inputAmount) && feeData.Fee) {
			setFeeData({ ...feeData, Fee: 0 });
		}
	}, [feeData]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (modals.length) removeNotEnoughNotification();
	}, [modals]); // eslint-disable-line react-hooks/exhaustive-deps

	const valueBoxWidth = valueBox.current?.offsetWidth || 100000;
	const amountFontMinSize = Math.max(
		20,
		(valueBoxWidth / (inputAmount.length + currencyData.Name.length)) * 1.7
	);
	const amountFontSize = Math.min(40, amountFontMinSize);

	return (
		<div
			className={classNames('SendAmountInput', {
				'SendAmountInput__amount-error': amountError,
			})}
		>
			<div className='SendAmountInput__available'>
				<div className='SendAmountInput__available-label'>
					{t('Available_v2')}:
				</div>
				<div className='SendAmountInput__available-amount'>
					{formatToFloor(account.Amount, currencyPrecision)}
				</div>
				<div className='SendAmountInput__available-currency'>
					{currencyData.Name}
				</div>
			</div>
			<div className='SendAmountInput__main-container'>
				<div
					className='SendAmountInput__value-box'
					ref={valueBox}
					style={{ fontSize: amountFontSize }}
				>
					<input
						className='SendAmountInput__input'
						value={inputAmount}
						onChange={setAmount}
						inputMode='decimal'
						maxLength={16}
						ref={inputRef}
						style={{ fontSize: amountFontSize }}
						onClick={focusInput}
						onFocus={focusInput}
					/>
					<div className='SendAmountInput__input-value'>{inputAmount}</div>
					<div className='SendAmountInput__input-currency'>{inputCurrency}</div>
				</div>
				<Button
					className='SendAmountInput__max'
					styleType='tertiary'
					onClick={setMaxAmount}
					btnSize='small'
					disabled={isMaxAmountSetted}
				>
					{t('Max')}
				</Button>
			</div>
			{usdAmount > 0 && minAmountUsd > usdAmount ? (
				<div className='SendAmountInput__min-amount-error'>
					{t('The minimum top-up amount is {amountUSD} USD', {
						amountUSD: minAmountUsd,
					})}
				</div>
			) : (
				<div className='SendAmountInput__converted'>
					{inputAmount === '0' ? (
						<div>
							{`1 ${inputCurrency} = ${formatToFloor(
								inputInUsd ? 1 / currencyRate : currencyRate,
								convertedPrecision
							)} ${convertedCurrency}`}
						</div>
					) : (
						<div>
							{formatToFloor(convertedAmountString, convertedPrecision)}{' '}
							{convertedCurrency}
						</div>
					)}
					<SwapButton onClick={swapInputCurrency} />
				</div>
			)}
		</div>
	);
};

export default SendAmountInput;
